// @ts-nocheck
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../hooks/useAuth';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface PairData {
  pair: string;
  coin_a: {
    id: string;
    symbol: string;
    name: string;
    current_price: number;
  };
  coin_b: {
    id: string;
    symbol: string;
    name: string;
    current_price: number;
  };
  signal: string;
  signal_type: string;
  score: number;
  signal_duration: number;
  confidence: string;
  ratio_analysis: {
    current: number;
    mean_90d: number;
    z_score: number;
    change_10d_pct: number;
  };
  technical: {
    ma_7: number;
    ma_10: number;
    ma_50: number;
    rsi: number;
    macd_bullish: boolean;
    bb_position: number;
  };
  obv: {
    coin_a_trend: string;
    coin_b_trend: string;
  };
  signals: string[];
  levels: {
    entry: number;
    target: number;
    stop: number;
  };
  analyzed_at: string;
}

interface AnalysisResponse {
  summary: {
    total_pairs: number;
    long_signals: number;
    short_signals: number;
    no_trade: number;
    top_opportunity: string | null;
  };
  pairs: PairData[];
}

const PairsAnalysis = () => {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AnalysisResponse | null>(null);
  const [selectedPair, setSelectedPair] = useState<PairData | null>(null);
  const [filterType, setFilterType] = useState<'all' | 'long' | 'short'>('all');

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get<AnalysisResponse>(
        `${API_BASE}/api/pairs/analysis-public`
      );
      setData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getScoreColor = (score: number) => {
    const absScore = Math.abs(score);
    if (absScore >= 60) return 'text-green-400';
    if (absScore >= 40) return 'text-yellow-400';
    return 'text-gray-400';
  };

  const getSignalBadge = (signalType: string, score: number) => {
    if (signalType === 'long') {
      return <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs font-medium">LONG</span>;
    } else if (signalType === 'short') {
      return <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs font-medium">SHORT</span>;
    }
    return <span className="px-2 py-1 bg-gray-500/20 text-gray-400 rounded text-xs font-medium">NO TRADE</span>;
  };

  const filteredPairs = data?.pairs.filter(pair => {
    if (filterType === 'all') return true;
    if (filterType === 'long') return pair.score > 0 && pair.signal_type !== 'neutral';
    if (filterType === 'short') return pair.score < 0 && pair.signal_type !== 'neutral';
    return true;
  }) || [];

  return (
    <Layout>
      <div className={`min-h-screen ${isDark ? 'bg-zinc-900' : 'bg-gray-50'}`}>
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-600 via-amber-600 to-yellow-600 opacity-10"></div>
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iIzAwMCIgb3BhY2l0eT0iLjA1Ii8+PC9nPjwvc3ZnPg==')] opacity-30"></div>

          <div className="relative max-w-7xl mx-auto px-6 py-12">
            <div className="text-center">
              <h1 className="text-5xl md:text-6xl font-bold mb-4">
                <span className="bg-gradient-to-r from-orange-600 via-amber-600 to-yellow-600 bg-clip-text text-transparent">
                  Pairs Trading
                </span>
                <br />
                <span className={isDark ? 'text-white' : 'text-gray-900'}>Analysis</span>
              </h1>
              <p className={`text-lg mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Statistical arbitrage opportunities across top 50 cryptocurrencies
              </p>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-orange-500/10 to-yellow-500/10 border border-orange-500/20">
                <span className="text-2xl">🔄</span>
                <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  1,225 Pairs Analyzed
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 py-8">
        {error && (
          <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Summary Cards */}
        {data && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-800 p-4 rounded-lg">
              <p className="text-gray-400 text-sm">Total Pairs</p>
              <p className="text-2xl font-bold text-white">{data.summary.total_pairs}</p>
            </div>
            <div className="bg-gray-800 p-4 rounded-lg">
              <p className="text-gray-400 text-sm">Long Signals</p>
              <p className="text-2xl font-bold text-green-400">{data.summary.long_signals}</p>
            </div>
            <div className="bg-gray-800 p-4 rounded-lg">
              <p className="text-gray-400 text-sm">Short Signals</p>
              <p className="text-2xl font-bold text-red-400">{data.summary.short_signals}</p>
            </div>
            <div className="bg-gray-800 p-4 rounded-lg">
              <p className="text-gray-400 text-sm">Top Opportunity</p>
              <p className="text-xl font-bold text-blue-400">{data.summary.top_opportunity || '-'}</p>
            </div>
          </div>
        )}

        {/* Filter Buttons */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setFilterType('all')}
            className={`px-4 py-2 rounded-lg ${filterType === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'}`}
          >
            All
          </button>
          <button
            onClick={() => setFilterType('long')}
            className={`px-4 py-2 rounded-lg ${filterType === 'long' ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300'}`}
          >
            Long Only
          </button>
          <button
            onClick={() => setFilterType('short')}
            className={`px-4 py-2 rounded-lg ${filterType === 'short' ? 'bg-red-600 text-white' : 'bg-gray-700 text-gray-300'}`}
          >
            Short Only
          </button>
        </div>

        {/* Pairs Table */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="text-gray-400 mt-4">Analyzing pairs... This may take a minute.</p>
          </div>
        ) : (
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Pair</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Signal</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-300 uppercase">Score</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-300 uppercase">Duration</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-300 uppercase">Confidence</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-300 uppercase">Z-Score</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-300 uppercase">10D Change</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-300 uppercase">RSI</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-300 uppercase">MACD</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-300 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filteredPairs.map((pair) => (
                  <tr key={pair.pair} className="hover:bg-gray-700/50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-white">{pair.pair}</div>
                      <div className="text-xs text-gray-400">
                        {pair.coin_a.name} / {pair.coin_b.name}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {getSignalBadge(pair.signal_type, pair.score)}
                    </td>
                    <td className={`px-4 py-3 text-center font-bold ${getScoreColor(pair.score)}`}>
                      {pair.score > 0 ? '+' : ''}{pair.score}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-300">
                      {pair.signal_duration > 0 ? `${pair.signal_duration}d` : '-'}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-300">{pair.confidence}</td>
                    <td className={`px-4 py-3 text-center ${pair.ratio_analysis.z_score < -1 ? 'text-green-400' : pair.ratio_analysis.z_score > 1 ? 'text-red-400' : 'text-gray-300'}`}>
                      {pair.ratio_analysis.z_score.toFixed(2)}
                    </td>
                    <td className={`px-4 py-3 text-center ${pair.ratio_analysis.change_10d_pct > 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {pair.ratio_analysis.change_10d_pct > 0 ? '+' : ''}{pair.ratio_analysis.change_10d_pct.toFixed(1)}%
                    </td>
                    <td className="px-4 py-3 text-center text-gray-300">{pair.technical.rsi.toFixed(0)}</td>
                    <td className="px-4 py-3 text-center">
                      {pair.technical.macd_bullish ? (
                        <span className="text-green-400">Bullish</span>
                      ) : (
                        <span className="text-red-400">Bearish</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => setSelectedPair(pair)}
                        className="text-blue-400 hover:text-blue-300 text-sm"
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Details Modal */}
        {selectedPair && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-white">{selectedPair.pair}</h2>
                    <p className="text-gray-400">{selectedPair.coin_a.name} / {selectedPair.coin_b.name}</p>
                  </div>
                  <button
                    onClick={() => setSelectedPair(null)}
                    className="text-gray-400 hover:text-white"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Signal */}
                <div className="bg-gray-700 p-4 rounded-lg mb-4">
                  <p className="text-lg font-bold text-white mb-2">{selectedPair.signal}</p>
                  <div className="flex gap-4">
                    <div>
                      <span className="text-gray-400 text-sm">Score:</span>
                      <span className={`ml-2 font-bold ${getScoreColor(selectedPair.score)}`}>
                        {selectedPair.score > 0 ? '+' : ''}{selectedPair.score}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Confidence:</span>
                      <span className="ml-2 text-white">{selectedPair.confidence}</span>
                    </div>
                  </div>
                </div>

                {/* Ratio Analysis */}
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-white mb-2">Ratio Analysis</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-700 p-3 rounded">
                      <p className="text-gray-400 text-xs">Current Ratio</p>
                      <p className="text-white font-medium">{selectedPair.ratio_analysis.current.toFixed(6)}</p>
                    </div>
                    <div className="bg-gray-700 p-3 rounded">
                      <p className="text-gray-400 text-xs">90-Day Mean</p>
                      <p className="text-white font-medium">{selectedPair.ratio_analysis.mean_90d.toFixed(6)}</p>
                    </div>
                    <div className="bg-gray-700 p-3 rounded">
                      <p className="text-gray-400 text-xs">Z-Score</p>
                      <p className={`font-medium ${selectedPair.ratio_analysis.z_score < -1 ? 'text-green-400' : selectedPair.ratio_analysis.z_score > 1 ? 'text-red-400' : 'text-white'}`}>
                        {selectedPair.ratio_analysis.z_score.toFixed(2)}
                      </p>
                    </div>
                    <div className="bg-gray-700 p-3 rounded">
                      <p className="text-gray-400 text-xs">10-Day Change</p>
                      <p className={`font-medium ${selectedPair.ratio_analysis.change_10d_pct > 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {selectedPair.ratio_analysis.change_10d_pct > 0 ? '+' : ''}{selectedPair.ratio_analysis.change_10d_pct.toFixed(1)}%
                      </p>
                    </div>
                  </div>
                </div>

                {/* Technical Indicators */}
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-white mb-2">Technical Indicators</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gray-700 p-3 rounded">
                      <p className="text-gray-400 text-xs">RSI</p>
                      <p className="text-white font-medium">{selectedPair.technical.rsi.toFixed(0)}</p>
                    </div>
                    <div className="bg-gray-700 p-3 rounded">
                      <p className="text-gray-400 text-xs">MACD</p>
                      <p className={`font-medium ${selectedPair.technical.macd_bullish ? 'text-green-400' : 'text-red-400'}`}>
                        {selectedPair.technical.macd_bullish ? 'Bullish' : 'Bearish'}
                      </p>
                    </div>
                    <div className="bg-gray-700 p-3 rounded">
                      <p className="text-gray-400 text-xs">BB Position</p>
                      <p className="text-white font-medium">{selectedPair.technical.bb_position.toFixed(0)}%</p>
                    </div>
                  </div>
                </div>

                {/* OBV */}
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-white mb-2">Volume Analysis (OBV)</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-700 p-3 rounded">
                      <p className="text-gray-400 text-xs">{selectedPair.coin_a.symbol} OBV</p>
                      <p className={`font-medium ${selectedPair.obv.coin_a_trend === 'Bullish' ? 'text-green-400' : 'text-red-400'}`}>
                        {selectedPair.obv.coin_a_trend}
                      </p>
                    </div>
                    <div className="bg-gray-700 p-3 rounded">
                      <p className="text-gray-400 text-xs">{selectedPair.coin_b.symbol} OBV</p>
                      <p className={`font-medium ${selectedPair.obv.coin_b_trend === 'Bullish' ? 'text-green-400' : 'text-red-400'}`}>
                        {selectedPair.obv.coin_b_trend}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Trading Levels */}
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-white mb-2">Trading Levels</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gray-700 p-3 rounded">
                      <p className="text-gray-400 text-xs">Entry</p>
                      <p className="text-white font-medium">{selectedPair.levels.entry.toFixed(6)}</p>
                    </div>
                    <div className="bg-gray-700 p-3 rounded">
                      <p className="text-gray-400 text-xs">Target</p>
                      <p className="text-green-400 font-medium">{selectedPair.levels.target.toFixed(6)}</p>
                    </div>
                    <div className="bg-gray-700 p-3 rounded">
                      <p className="text-gray-400 text-xs">Stop Loss</p>
                      <p className="text-red-400 font-medium">{selectedPair.levels.stop.toFixed(6)}</p>
                    </div>
                  </div>
                </div>

                {/* Signals List */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Analysis Signals</h3>
                  <ul className="space-y-2">
                    {selectedPair.signals.map((signal, index) => (
                      <li key={index} className="flex items-start text-sm text-gray-300">
                        <span className="text-blue-400 mr-2">•</span>
                        {signal}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </Layout>
  );
};

export default PairsAnalysis;
